import { FC, ReactNode, createContext, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";

import { useEventsByStatus } from "@/pages/Sport/hooks/useEventsByStatus";
import { useSportEvents } from "@/pages/Sport/hooks/useSportEvents";
import { useSportSocket } from "@/pages/Sport/hooks/useSportSocket";
import { useSports } from "@/pages/Sport/hooks/useSports";
import { EventStatus } from "@/pages/Sport/types";
import { alignSportCounts, freshData, isListedStatus } from "@/pages/Sport/utils/sportCounts";
import { Sport } from "@/types";

export interface SportsBookContextProps {
  sport: Sport | undefined;
  sports: Sport[];
  eventStatus: EventStatus;
  sportSlug: string;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  totalInPlayCount: number;
  totalNext24Count: number;
  totalUpcomingCount: number;

  getSport: (sportSlug: string) => Sport | undefined;
}

export const SportsBookContext = createContext<SportsBookContextProps | undefined>(undefined);

export const SportsBookProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const {
    sportSlug = "football",
    eventStatus = "live",
    eventId,
  } = useParams<{
    sportSlug: string;
    eventStatus: EventStatus;
    eventId: string;
  }>();
  // One push connection for every sport page: the in-play list (counts, live pages) and the open match.
  useSportSocket({
    live: true,
    eventIds: eventId && /^\d+$/.test(eventId) ? [Number(eventId)] : [],
  });
  const { data, isLoading, isError: failed, refetch: refetchSports } = useSports("all");
  // A failed timed refresh keeps the last list instead of swapping the page for an error.
  const isError = failed && !data;
  // Live is fetched here so In-Play always counts what its page lists; the rest count from a page once it is loaded.
  const { data: liveData } = useEventsByStatus("live");
  const next24 = useEventsByStatus("next24", { fetch: false });
  const upcoming = useEventsByStatus("upcoming", { fetch: false });
  const sportList = useSportEvents(sportSlug, eventStatus, { fetch: false });

  const now = Date.now();
  const next24Data = freshData(next24, "next24", now);
  const upcomingData = freshData(upcoming, "upcoming", now);
  const shownCount = isListedStatus(eventStatus)
    ? freshData(sportList, eventStatus, now)?.eventCount
    : undefined;

  const { sports, totalInPlayCount, totalNext24Count, totalUpcomingCount } = useMemo(
    () =>
      alignSportCounts(
        data,
        { live: liveData, next24: next24Data, upcoming: upcomingData },
        shownCount != null && isListedStatus(eventStatus)
          ? { slug: sportSlug, status: eventStatus, eventCount: shownCount }
          : undefined,
      ),
    [data, liveData, next24Data, upcomingData, shownCount, sportSlug, eventStatus],
  );

  const refetch = useCallback(() => void refetchSports(), [refetchSports]);

  const getSport = useCallback(
    (slug: string) => sports.find((sport) => sport.slug === slug),
    [sports],
  );

  const sport = useMemo(() => getSport(sportSlug), [getSport, sportSlug]);

  const value = useMemo(
    () => ({
      sport,
      eventStatus,
      sportSlug,
      sports,
      isLoading,
      isError,
      refetch,
      getSport,
      totalInPlayCount,
      totalNext24Count,
      totalUpcomingCount,
    }),
    [
      sport,
      sportSlug,
      eventStatus,
      sports,
      isLoading,
      isError,
      refetch,
      getSport,
      totalInPlayCount,
      totalNext24Count,
      totalUpcomingCount,
    ],
  );

  return <SportsBookContext.Provider value={value}>{children}</SportsBookContext.Provider>;
};
